import { Select } from 'antd';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  createSemesterActivity,
  deleteSemesterActivity,
  getActivityItems,
  getActivityItemsHW,
  getSemesterTimetables,
  updateSemesterActivity,
} from '../../../api/semesterProduct.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import {
  IActivityItem,
  IActivityItemsHW,
  IGetActivityItem,
  IGetActivityItems,
  IGetActivityItemsHW,
  IGetSemesterTimetables,
} from '../../../models/semesterProduct.model';
import { RootState } from '../../../store/rootReducer';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import EvaluationTable from './EvaluationTable';
import EvaluationHWTable from './EvaluationHWTable';
import { IGetEvaluationItemTree } from '../../../models/evaluationItem.model';
import { getEvaluationItemTree } from '../../../api/evaluationItem.api';

interface IProps {
  chosenCourseList: any;
  semesterInput: any;
  courseInput: any;
}

const Evaluation: FC<IProps> = ({
  chosenCourseList,
  semesterInput,
  courseInput,
}) => {
  const { t } = useTranslation();
  const { successNotification, infoNotification } = useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetSemesterTimetables>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetActivityItems>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetEvaluationItemTree>();
  const {
    data: res4,
    isLoading: isLoading4,
    error: error4,
    execute: execute4,
  } = useAxios<IGetActivityItemsHW>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios<IGetActivityItem>();
  const {
    data: deleteRes,
    isLoading: deleteIsLoading,
    error: deleteError,
    execute: deleteExecute,
  } = useAxios();

  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // GR0001 = 평가 구분
  const GR0001 = commonCodes.find(
    (item) => item.commonCode === 'GR0001',
  )?.detailCode;
  const [learningYearCode, setLearningYearCode] = useState('');
  const [semesterGBN, setSemesterGBN] = useState('');
  const [courseCode, setCourseCode] = useState<number>();
  const [courseIndex, setCourseIndex] = useState<number>();
  const [learningTypeCode, setLearningTypeCode] = useState<string>();
  const [semesterCourseCode, setSemesterCourseCode] = useState<number>();
  const [data, setData] = useState<IActivityItem[]>([]);
  // 삭제 시 사용
  const [semesterSeqEvaluationCode, setSemesterSeqEvaluationCode] =
    useState<number>();
  // 저장 시 사용
  const [prdCurriculumCode, setPrdCurriculumCode] = useState<number>();
  const [timetableSeq, setTimetableSeq] = useState<number>();

  // 학기 선택 변경 시 대응
  useEffect(() => {
    setCourseIndex(undefined);
    setLearningTypeCode(undefined);
  }, [courseInput]);

  useEffect(() => {
    if (semesterInput) {
      setLearningYearCode(semesterInput.year);
      setSemesterGBN(semesterInput.semesterDiv);
    }
  }, [semesterInput]);

  useEffect(() => {
    if (semesterCourseCode) {
      execute(getSemesterTimetables(semesterCourseCode));
    }
  }, [semesterCourseCode]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      setData(_.cloneDeep(res2.data));
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      successNotification(t('common.message.delete'));
      const _data = [...data];
      const target = _data.find(
        (item) => item.semesterSeqEvaluationCode === semesterSeqEvaluationCode,
      );
      if (target) {
        target.monthSeq = null;
        target.timetableSeq = null;
      }
      setData(_data);
      if (timetableSeq && semesterCourseCode)
        execute4(getActivityItemsHW({ timetableSeq, semesterCourseCode }));
    }
  }, [deleteRes, deleteIsLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.save1'));
      const _data = [...data];
      const target = _data.find(
        (item) => item.prdCurriculumCode === prdCurriculumCode,
      );
      if (target) {
        target.semesterSeqEvaluationCode =
          submitRes.data.semesterSeqEvaluationCode;
      }
      setData(_data);
      if (timetableSeq && semesterCourseCode) {
        execute4(getActivityItemsHW({ timetableSeq, semesterCourseCode }));
      }
    }
  }, [submitRes, submitIsLoading]);

  const onSetLearningTypeCode = (value: string) => {
    if (
      !learningYearCode ||
      !semesterGBN ||
      !courseCode ||
      !value ||
      !semesterCourseCode
    )
      return;

    if (value === 'HW') {
      execute3(getEvaluationItemTree('HW'));
    } else {
      execute2(
        getActivityItems({
          learningYearCode,
          semesterGBN,
          courseCode,
          learningTypeCode: value,
          semesterCourseCode,
        }),
      );
    }
    setLearningTypeCode(value);
  };

  const onSetData = (index: number, key: string, value: any) => {
    const _data = [...data];
    // @ts-ignore
    _data[index][key] = value;
    if (key === 'monthSeq') {
      _data[index].timetableSeq = null;
    }
    setData(_data);
  };

  const onDelete = (value: number) => {
    setSemesterSeqEvaluationCode(value);
    deleteExecute(deleteSemesterActivity(value));
  };

  const onSubmit = (record: IActivityItem) => {
    if (!semesterCourseCode || !record.timetableSeq) return;

    setPrdCurriculumCode(record.prdCurriculumCode);

    if (record.semesterSeqEvaluationCode) {
      // update
      submitExecute(
        updateSemesterActivity(
          semesterCourseCode,
          record.semesterSeqEvaluationCode,
          {
            timetableSeq: record.timetableSeq,
          },
        ),
      );
    } else {
      // create
      submitExecute(
        createSemesterActivity(semesterCourseCode, record.timetableSeq, {
          prdCurriculumCode: record.prdCurriculumCode,
          learningTypeCode,
        }),
      );
    }
  };

  const onHWSubmit = (record: IActivityItemsHW) => {
    if (semesterCourseCode && timetableSeq) {
      if (record.evaluationItemCode === 0)
        return infoNotification(t('program.semester.message18'));
      if (record.semesterSeqEvaluationCode === 0) {
        submitExecute(
          createSemesterActivity(semesterCourseCode, timetableSeq, {
            learningTypeCode,
            evaluationItemCode: record.evaluationItemCode,
          }),
        );
      } else {
        submitExecute(
          updateSemesterActivity(
            semesterCourseCode,
            record.semesterSeqEvaluationCode,
            {
              timetableSeq,
              evaluationItemCode: record.evaluationItemCode,
            },
          ),
        );
      }
      setTimetableSeq(timetableSeq);
    }
  };

  const onRowClick = (timetableSeq: number) => {
    setTimetableSeq(timetableSeq);
    semesterCourseCode &&
      execute4(getActivityItemsHW({ timetableSeq, semesterCourseCode }));
  };

  return (
    <>
      {(isLoading ||
        isLoading2 ||
        isLoading3 ||
        isLoading4 ||
        submitIsLoading ||
        deleteIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={deleteError} />

      <div className="flex flex-justify-end mb-3">
        <div className="w-150p mr-1">
          <Select
            className="w-100"
            placeholder={t('program.semester.placeholder1')}
            value={courseIndex}
            onChange={(value) => {
              setLearningTypeCode(undefined);
              const course = chosenCourseList.find(
                (item: any) => item.value === value,
              );
              if (course) {
                setData([]);
                setCourseIndex(value);
                setCourseCode(course.courseCode);
                setSemesterCourseCode(courseInput[value].id);
              }
            }}
          >
            {chosenCourseList?.map((item: any) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="w-150p mr-1">
          <Select
            className="w-100"
            placeholder={t('program.semester.placeholder2')}
            value={learningTypeCode}
            onChange={(value) => onSetLearningTypeCode(value)}
            disabled={!courseCode}
          >
            {GR0001?.map((item) => (
              <Select.Option key={item.detailCode} value={item.detailCode}>
                {item.korName}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div>
        {learningTypeCode === 'HW' ? (
          <EvaluationHWTable
            semesterTimetables={res?.data ?? []}
            evaluationItemTree={res3?.data ?? []}
            activityItemsHW={res4?.data ?? []}
            onRowClick={onRowClick}
            onDelete={onDelete}
            onSubmit={onHWSubmit}
          />
        ) : (
          <EvaluationTable
            semesterTimetables={res?.data ?? []}
            onSetData={onSetData}
            onDelete={onDelete}
            onSubmit={onSubmit}
            data={data}
          />
        )}
      </div>
    </>
  );
};

export default Evaluation;
